import { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"

import TrailerModal from "../../components/TrailerModal"
import TrailerPlayer from "../../components/TrailerPlayer"
import ResponsiveFilmPoster, { filmPosterData } from "../../components/ResponsiveFilmPoster"
import { Like, LikeFilled } from "../../components/svgCollection"
import PrivilegeTag, { PRIVILEGE_TAGS } from "../../features/film/PrivilegeTag"
import FilmMetaAboveTitle from "../../features/film/FilmMetaAboveTitle"
import StarRating from "../../features/film/StarRating"
import FilmScreenshots from "../../features/film/FilmScreenshots"
import { SessionString } from "../../features/home"
import { setFilmMetaCache } from "../../lib/filmMetaCache"
import { applyDocumentSeo, buildMovieJsonLd, excerpt, SITE_NAME } from "../../lib/siteSeo"
import { useCinemaHref, useCinemaSlug } from "../../hooks/useCinemaHref"
import { cinemaHref } from "../../lib/cinemaSlugs"
import {
    filterRealComments,
    isCommentsDisabledStub,
    substituteDisabledCommentBodies,
} from "../../lib/commentsDisabledPlaceholder"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { userStore } from "../../store/userStore"
import { FilmComment, FilmDetail } from "../../types/entities/filmDetail"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

function InfoBlock({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-5">
            <h2
                className="mb-3 text-white/35 text-[10px] uppercase tracking-[0.1em]"
                style={{ fontFamily: MONO, fontWeight: 700 }}>
                {title}
            </h2>
            <div className="text-[15px] leading-relaxed text-white/75" style={{ fontFamily: BODY, fontWeight: 500 }}>
                {children}
            </div>
        </section>
    )
}

function RatingRow({ source, value, scale }: { source: string; value: number; scale: string }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-[14px] border border-white/[0.07] bg-white/[0.03] px-3 py-2.5">
            <span className="text-white/55 text-[12px]" style={{ fontFamily: BODY, fontWeight: 600 }}>
                {source}
            </span>
            <span className="whitespace-nowrap" style={{ fontFamily: MONO, fontWeight: 700 }}>
                <span className="text-white text-[16px]">{value}</span>
                <span className="text-white/35 text-[12px]"> {scale}</span>
            </span>
        </div>
    )
}

function FilmRatings({ ratings }: { ratings: FilmDetail["ratings"] }) {
    const items = [
        ratings.kinopoisk != null && { source: "Кинопоиск", value: ratings.kinopoisk, scale: "/ 10" },
        ratings.imdb != null && { source: "IMDb", value: ratings.imdb, scale: "/ 10" },
        ratings.comments != null && { source: "Мягкий кинотеатр", value: ratings.comments, scale: "/ 5" },
    ].filter(Boolean) as { source: string; value: number; scale: string }[]

    if (items.length === 0) return null

    return (
        <div className="flex flex-wrap gap-2">
            {items.map(item => (
                <div key={item.source} className="min-w-[calc(50%-4px)] flex-1 sm:min-w-[140px] sm:max-w-[220px]">
                    <RatingRow source={item.source} value={item.value} scale={item.scale} />
                </div>
            ))}
        </div>
    )
}

export default function Film() {
    const { filmId } = useParams<{ filmId: string }>()
    const location = useLocation()
    const navigate = useNavigate()
    const href = useCinemaHref()
    const cinemaSlug = useCinemaSlug()
    const { cinema } = appStore()
    const { user } = userStore()
    const { request, requestData } = useRequest()
    const isMobile = window.matchMedia("(max-width: 768px)").matches

    const [film, setFilm] = useState<FilmDetail | null>(null)
    const [comments, setComments] = useState<FilmComment[]>([])
    const [loading, setLoading] = useState(true)
    const [loadingMoreComments, setLoadingMoreComments] = useState(false)
    const [hasMoreComments, setHasMoreComments] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isFavorite, setIsFavorite] = useState(false)
    const [togglingFavorite, setTogglingFavorite] = useState(false)
    const [showAllSessions, setShowAllSessions] = useState(false)
    const [showTrailer, setShowTrailer] = useState(false)

    const loadComments = useCallback(
        async (lastId?: number) => {
            if (!filmId) return []
            const path = lastId
                ? `cinema/${cinema.id}/film/${filmId}/comment?last_id=${lastId}`
                : `cinema/${cinema.id}/film/${filmId}/comment`
            const commentsRes = await requestData<FilmComment[]>({
                type: "GET",
                path,
                disableError: true,
            })
            if (commentsRes.status !== "success" || !commentsRes.data) {
                if (!lastId) {
                    setComments([])
                    setHasMoreComments(false)
                }
                return []
            }
            const data = commentsRes.data
            if (lastId) {
                setComments(prev => [...prev, ...data])
            } else {
                setComments(data)
            }
            // Страница по 10, как история покупок (в т.ч. когда отзывы — заглушки API)
            setHasMoreComments(data.length === 10)
            return data
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cinema.id, filmId],
    )

    useEffect(() => {
        if (!filmId) return
        const state = location.state as { countries?: string; duration?: string } | null
        if (state?.countries || state?.duration) {
            setFilmMetaCache(cinema.id, Number(filmId), {
                countries: state.countries,
                duration: state.duration,
            })
        }
    }, [filmId, cinema.id, location.state])

    useEffect(() => {
        if (!filmId) return
        let cancelled = false

        const load = async () => {
            setLoading(true)
            setError(null)
            setComments([])
            setHasMoreComments(false)
            try {
                const data = await request<FilmDetail>("GET", `cinema/${cinema.id}/film/${filmId}`)
                if (cancelled) return
                setFilm(data)
                setIsFavorite(data.favourites)
                setFilmMetaCache(cinema.id, data.id, {
                    countries: data.countries,
                    duration: data.duration,
                })

                await loadComments()
            } catch (e) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : "Не удалось загрузить фильм")
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filmId, cinema.id, loadComments])

    useEffect(() => {
        if (!film) return
        const description =
            excerpt(film.description) ||
            `Сеанс «${film.name}» в сети «Мягкий кинотеатр»: расписание и билеты онлайн.`
        applyDocumentSeo({
            title: `${film.name} — ${SITE_NAME}`,
            description,
            path: cinemaHref(cinemaSlug, `/afisha/film/${film.id}`),
            image: film.poster?.big,
        })
    }, [film, cinemaSlug])

    const loadMoreComments = async () => {
        const lastItem = comments[comments.length - 1]
        if (!lastItem || loadingMoreComments) return
        setLoadingMoreComments(true)
        try {
            await loadComments(lastItem.id)
        } finally {
            setLoadingMoreComments(false)
        }
    }

    const sessionGroups = useMemo(() => {
        if (!film?.sessions) return []
        return Object.entries(film.sessions).map(([label, sessions]) => ({ label, sessions }))
    }, [film])

    const commentsDisabled = isCommentsDisabledStub(comments)
    const displayedComments = useMemo(
        () =>
            commentsDisabled
                ? substituteDisabledCommentBodies(comments, Number(filmId) || undefined)
                : filterRealComments(comments),
        [commentsDisabled, comments, filmId],
    )

    const visibleSessionGroups = showAllSessions ? sessionGroups : sessionGroups.slice(0, 3)

    const trailerSrc = film?.trailer
    const closeTrailer = useCallback(() => setShowTrailer(false), [])

    const toggleFavorite = async () => {
        if (!film || togglingFavorite) return
        if (!user) {
            navigate("/auth")
            return
        }
        setTogglingFavorite(true)
        try {
            if (isFavorite) {
                await request("DELETE", "user/favorite", { film_id: film.id })
            } else {
                await request("POST", "user/favorite", { film_id: film.id })
            }
            setIsFavorite(v => !v)
        } finally {
            setTogglingFavorite(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-4 px-4 py-4 md:py-6">
                <div className="relative flex flex-col gap-4 md:flex-row rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-5">
                    <div className="shrink-0 self-start w-full aspect-[650/348] md:w-[275px] md:aspect-[473/677] rounded-[16px] bg-white/[0.06] animate-pulse" />
                    <div className="flex w-full flex-col gap-3 min-w-0">
                        <div className="h-5 w-2/3 max-w-[280px] rounded-lg bg-white/[0.06] animate-pulse" />
                        <div className="h-4 w-1/2 max-w-[200px] rounded-lg bg-white/[0.04] animate-pulse" />
                        <div className="h-4 w-1/3 max-w-[160px] rounded-lg bg-white/[0.04] animate-pulse" />
                        <div className="mt-2 flex flex-wrap gap-2">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="h-8 w-14 rounded-[10px] bg-white/[0.06] animate-pulse" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (error || !film) {
        return (
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 px-4 py-20 text-center">
                <span className="text-[56px]">🎬</span>
                <p className="text-white/50 text-[15px]" style={{ fontFamily: BODY }}>
                    {error ?? "Фильм не найден"}
                </p>
                <Link
                    to={href("/afisha")}
                    className="rounded-full px-6 py-3 text-white text-[13px]"
                    style={{ background: "#349be6", fontFamily: MONO, fontWeight: 700 }}>
                    В афишу
                </Link>
            </div>
        )
    }

    const activeTags = PRIVILEGE_TAGS.filter(t => film.privileges?.[t.key])
    const movieData = {
        privileges: film.privileges,
        ...filmPosterData(film),
    }
    const hasMeta = Boolean(film.directors || film.actors)
    const hasRatings =
        film.ratings.kinopoisk != null || film.ratings.imdb != null || film.ratings.comments != null
    const hasFilmInfo = Boolean(film.countries || film.duration || film.genres)
    const hasScreenshots = film.screenshots.length > 0
    const hasSideColumn = hasMeta || hasFilmInfo

    const movieLd = JSON.stringify(buildMovieJsonLd(film, cinemaSlug))

    return (
        <div className="flex flex-col gap-4 px-4 py-4 md:py-6">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: movieLd }} />
            {/* Карточка — как в афише */}
            <div className="relative flex flex-col gap-5 md:flex-row md:gap-8 rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-6 group">
                <div className="absolute inset-0 rounded-[24px] overflow-hidden pointer-events-none">
                    <div
                        className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ background: "linear-gradient(135deg, rgba(52,155,230,0.08) 0%, transparent 60%)" }}
                    />
                </div>

                {/* Постер / трейлер */}
                <div className="relative shrink-0 self-start w-full overflow-hidden rounded-[16px] aspect-[650/348] md:w-[275px] md:aspect-[473/677]">
                    {showTrailer && trailerSrc && isMobile ? (
                        <>
                            <TrailerPlayer
                                src={trailerSrc}
                                title={film.name}
                                className="absolute inset-0 h-full w-full rounded-[16px] border-0 bg-black"
                            />
                            <button
                                type="button"
                                onClick={closeTrailer}
                                aria-label="Закрыть трейлер"
                                className="absolute top-2 right-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full border border-white/25 bg-black/60 text-white/90 transition-colors hover:bg-black/80">
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                                    <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </button>
                        </>
                    ) : (
                        <>
                            <ResponsiveFilmPoster
                                film={film}
                                alt={film.name}
                                className="absolute inset-0 size-full rounded-[16px] object-cover"
                            />
                            {trailerSrc && (
                                <motion.button
                                    type="button"
                                    aria-label="Смотреть трейлер"
                                    whileHover={{ scale: 1.06 }}
                                    whileTap={{ scale: 0.94 }}
                                    onClick={() => setShowTrailer(true)}
                                    className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-[16px] bg-black/0 transition-colors hover:bg-black/20">
                                    <div
                                        className="flex size-12 items-center justify-center rounded-full border border-white bg-transparent"
                                        style={{ boxShadow: "0 0 24px rgba(52,155,230,0.35)" }}>
                                        <svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden>
                                            <path d="M1 1.5v13l11-6.5L1 1.5z" fill="white" stroke="white" strokeWidth="1" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </motion.button>
                            )}
                        </>
                    )}
                </div>

                {typeof document !== "undefined" &&
                    createPortal(
                        <AnimatePresence>
                            {showTrailer && trailerSrc && !isMobile && (
                                <TrailerModal key={film.id} title={film.name} src={trailerSrc} onClose={closeTrailer} />
                            )}
                        </AnimatePresence>,
                        document.body,
                    )}

                {/* Инфо */}
                <div className="flex w-full flex-col gap-5 min-w-0 md:gap-6">
                    <div className="min-w-0">
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <FilmMetaAboveTitle className="" countries={film.countries} duration={film.duration} />
                            <div className="ml-auto flex shrink-0 items-center gap-2">
                                <div
                                    className="flex size-9 items-center justify-center rounded-full border border-white/20 bg-white/5 text-[11px] text-white/80"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    {film.age}
                                </div>
                                <motion.button
                                    type="button"
                                    disabled={togglingFavorite}
                                    onClick={toggleFavorite}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.88 }}
                                    className={`flex items-center justify-center size-9 rounded-full border transition-all ${
                                        isFavorite
                                            ? "border-accent bg-accent/20"
                                            : "border-white/20 bg-white/5 opacity-60 hover:opacity-100"
                                    } ${togglingFavorite ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    style={{ boxShadow: isFavorite ? "0 0 14px rgba(228,32,107,0.4)" : "none" }}>
                                    {isFavorite ? <LikeFilled /> : <Like />}
                                </motion.button>
                            </div>
                        </div>

                        <h1
                            className="text-[24px] text-white leading-tight md:text-[28px]"
                            style={{
                                fontFamily: DISPLAY,
                                fontWeight: 800,
                                letterSpacing: "-0.02em",
                            }}>
                            {film.name}
                        </h1>
                    </div>

                    {activeTags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {activeTags.map(t => (
                                <PrivilegeTag key={t.key} label={t.label} color={t.color} />
                            ))}
                        </div>
                    )}

                    {hasRatings && (
                        <div className="hidden md:block">
                            <FilmRatings ratings={film.ratings} />
                        </div>
                    )}

                    {film.description && (
                        <div className="hidden flex-col gap-1.5 md:flex">
                            <p
                                className="text-[10px] uppercase tracking-[0.1em] text-white/35"
                                style={{ fontFamily: MONO, fontWeight: 700 }}>
                                Описание
                            </p>
                            <p
                                className="whitespace-pre-line text-[14px] leading-relaxed text-white/65"
                                style={{ fontFamily: BODY, fontWeight: 500 }}>
                                {film.description}
                            </p>
                        </div>
                    )}

                    {sessionGroups.length > 0 && (
                        <div className="flex flex-col gap-4">
                            {visibleSessionGroups.map(group => (
                                <div key={group.label} className="flex flex-col gap-2">
                                    <p className="text-[13px] text-white/50" style={{ fontFamily: MONO, fontWeight: 700 }}>
                                        {group.label}
                                    </p>
                                    <SessionString sessions={group.sessions} movieData={movieData} />
                                </div>
                            ))}
                            {sessionGroups.length > 3 && !showAllSessions && (
                                <motion.button
                                    type="button"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setShowAllSessions(true)}
                                    className="self-start rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-[12px] text-[#349be6]"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    Посмотреть все сеансы
                                </motion.button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Рейтинги на мобильных — отдельной карточкой под основной */}
            {hasRatings && (
                <div className="md:hidden">
                    <InfoBlock title="Рейтинги">
                        <FilmRatings ratings={film.ratings} />
                    </InfoBlock>
                </div>
            )}

            {/* Описание на мобильных — отдельной карточкой над блоком «Режиссёр» */}
            {film.description && (
                <div className="md:hidden">
                    <InfoBlock title="Описание">
                        <p className="whitespace-pre-line">{film.description}</p>
                    </InfoBlock>
                </div>
            )}

            {(hasSideColumn || hasScreenshots) && (
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    {hasSideColumn && (
                        <div className="flex flex-col gap-4 lg:w-[320px] lg:shrink-0">
                            {film.directors && <InfoBlock title="Режиссёр">{film.directors}</InfoBlock>}
                            {film.actors && <InfoBlock title="В ролях">{film.actors}</InfoBlock>}
                            {hasFilmInfo && (
                                <InfoBlock title="О фильме">
                                    <div className="flex flex-col gap-2">
                                        {film.countries && (
                                            <p>
                                                <span className="text-white/45">Страна: </span>
                                                {film.countries}
                                            </p>
                                        )}
                                        {film.duration && (
                                            <p>
                                                <span className="text-white/45">Время: </span>
                                                {film.duration}
                                            </p>
                                        )}
                                        {film.genres && (
                                            <p>
                                                <span className="text-white/45">Жанры: </span>
                                                {film.genres}
                                            </p>
                                        )}
                                    </div>
                                </InfoBlock>
                            )}
                        </div>
                    )}

                    {hasScreenshots && <FilmScreenshots screenshots={film.screenshots} />}
                </div>
            )}

            <section className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2
                            className="text-white/35 text-[10px] uppercase tracking-[0.1em]"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            Отзывы
                        </h2>
                        {displayedComments.length > 0 && (
                            <p className="mt-1 text-white/40 text-[13px]" style={{ fontFamily: BODY }}>
                                {hasMoreComments ? `Показано: ${displayedComments.length}` : `Отзывов: ${displayedComments.length}`}
                            </p>
                        )}
                    </div>
                    {film.comment_add && !user && !commentsDisabled && (
                        <button
                            type="button"
                            onClick={() => navigate("/auth")}
                            className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-[12px] text-[#349be6]"
                            style={{ fontFamily: MONO, fontWeight: 700 }}>
                            Написать отзыв
                        </button>
                    )}
                </div>

                {displayedComments.length === 0 ? (
                    <p className="text-white/35 text-[14px]" style={{ fontFamily: BODY }}>
                        Пока нет отзывов
                    </p>
                ) : (
                    <div className="flex flex-col gap-4">
                        {displayedComments.map(comment => (
                            <article
                                key={comment.id}
                                className="rounded-[16px] border border-white/[0.07] bg-white/[0.03] p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-white text-[14px]" style={{ fontFamily: DISPLAY, fontWeight: 700 }}>
                                        {comment.user_name}
                                    </p>
                                    <StarRating rate={comment.rate} my={comment.my} />
                                </div>
                            </article>
                        ))}
                        {hasMoreComments && (
                            <div className="flex justify-center pt-1">
                                <motion.button
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    type="button"
                                    onClick={loadMoreComments}
                                    disabled={loadingMoreComments}
                                    className="rounded-full px-6 py-2.5 text-[13px] border border-white/15 bg-white/[0.06] text-white/60 hover:text-white hover:border-white/40 transition-all disabled:opacity-50"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    {loadingMoreComments ? "Загружаю..." : "Показать ещё"}
                                </motion.button>
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    )
}
